import React, {
    useCallback,
    useEffect,
} from "react";

import {
    FiFileText,
    FiRefreshCw,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    fetchPurchaseGstInputReport,
    purchaseGstInputRecordsSelector,
    purchaseGstInputSummarySelector,
    purchaseReportLoadingSelector,
} from "../../../../../../../store/billing/purchaseReport/purchaseReportSlice";


const toNumber = (
    value
) =>
    Number.isFinite(
        Number(value)
    )
        ? Number(value)
        : 0;


const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
        }
    ).format(
        toNumber(value)
    );


const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }
    ).format(
        new Date(value)
    );
};


const GstInputReport = ({
    fromDate,
    toDate,
    financialYear,
}) => {
    const dispatch =
        useDispatch();

    const records =
        useSelector(
            purchaseGstInputRecordsSelector
        ) || [];

    const summary =
        useSelector(
            purchaseGstInputSummarySelector
        ) || {};

    const loading =
        useSelector(
            purchaseReportLoadingSelector
        ) || {};


    const loadData =
        useCallback(
            () => {
                dispatch(
                    fetchPurchaseGstInputReport({
                        fromDate,
                        toDate,
                        financialYear,
                        status: "CONFIRMED",
                        page: 1,
                        limit: 200,
                    })
                );
            },
            [
                dispatch,
                fromDate,
                toDate,
                financialYear,
            ]
        );


    useEffect(
        () => {
            loadData();
        },
        [
            loadData,
        ]
    );


    return (
        <div>

            <section className="purchase-report-summary-grid">

                <div className="purchase-report-summary-card">
                    <span>
                        Taxable Purchase
                    </span>

                    <strong>
                        {formatCurrency(
                            summary?.taxableAmount
                        )}
                    </strong>
                </div>


                <div className="purchase-report-summary-card">
                    <span>
                        CGST
                    </span>

                    <strong>
                        {formatCurrency(
                            summary?.cgstAmount
                        )}
                    </strong>
                </div>


                <div className="purchase-report-summary-card">
                    <span>
                        SGST
                    </span>

                    <strong>
                        {formatCurrency(
                            summary?.sgstAmount
                        )}
                    </strong>
                </div>


                <div className="purchase-report-summary-card">
                    <span>
                        IGST
                    </span>

                    <strong>
                        {formatCurrency(
                            summary?.igstAmount
                        )}
                    </strong>
                </div>

            </section>


            <section className="purchase-report-tax-summary">

                <div>
                    <span>
                        Cess
                    </span>

                    <strong>
                        {formatCurrency(
                            summary?.cessAmount
                        )}
                    </strong>
                </div>

                <div>
                    <span>
                        Input GST
                    </span>

                    <strong>
                        {formatCurrency(
                            summary?.inputTax
                        )}
                    </strong>
                </div>

                <div>
                    <span>
                        Purchase Total
                    </span>

                    <strong>
                        {formatCurrency(
                            summary?.grandTotal
                        )}
                    </strong>
                </div>

            </section>


            <section className="purchase-report-table-card">

                {loading.gstInput &&
                records.length === 0 ? (
                    <div className="purchase-report-empty">
                        <FiRefreshCw className="purchase-report-spinner" />

                        <strong>
                            Loading GST input...
                        </strong>
                    </div>
                ) : records.length === 0 ? (
                    <div className="purchase-report-empty">
                        <FiFileText />

                        <strong>
                            No GST purchase data
                        </strong>
                    </div>
                ) : (
                    <div className="purchase-report-table-wrapper">

                        <table className="purchase-report-table">

                            <thead>
                                <tr>
                                    <th>
                                        Bill
                                    </th>

                                    <th>
                                        Date
                                    </th>

                                    <th>
                                        Supplier
                                    </th>

                                    <th>
                                        GSTIN
                                    </th>

                                    <th className="text-right">
                                        Taxable
                                    </th>

                                    <th className="text-right">
                                        CGST
                                    </th>

                                    <th className="text-right">
                                        SGST
                                    </th>

                                    <th className="text-right">
                                        IGST
                                    </th>

                                    <th className="text-right">
                                        Cess
                                    </th>

                                    <th className="text-right">
                                        Total
                                    </th>
                                </tr>
                            </thead>


                            <tbody>
                                {records.map(
                                    (
                                        item,
                                        index
                                    ) => (
                                        <tr
                                            key={
                                                item?._id ||
                                                index
                                            }
                                        >
                                            <td>
                                                <strong className="purchase-report-bill-number">
                                                    {item?.documentNumber || "-"}
                                                </strong>
                                            </td>

                                            <td>
                                                {formatDate(
                                                    item?.documentDate
                                                )}
                                            </td>

                                            <td>
                                                {item?.supplier?.supplierName || "-"}
                                            </td>

                                            <td>
                                                {item?.supplier?.gstin || "-"}
                                            </td>

                                            <td className="text-right">
                                                {formatCurrency(
                                                    item?.taxableAmount
                                                )}
                                            </td>

                                            <td className="text-right">
                                                {formatCurrency(
                                                    item?.cgstAmount
                                                )}
                                            </td>

                                            <td className="text-right">
                                                {formatCurrency(
                                                    item?.sgstAmount
                                                )}
                                            </td>

                                            <td className="text-right">
                                                {formatCurrency(
                                                    item?.igstAmount
                                                )}
                                            </td>

                                            <td className="text-right">
                                                {formatCurrency(
                                                    item?.cessAmount
                                                )}
                                            </td>

                                            <td className="text-right">
                                                <strong>
                                                    {formatCurrency(
                                                        item?.grandTotal
                                                    )}
                                                </strong>
                                            </td>
                                        </tr>
                                    )
                                )}
                            </tbody>

                        </table>

                    </div>
                )}

            </section>

        </div>
    );
};


export default GstInputReport;